import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { jamboree2027PreviewLogo } from "../../../assets";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import { formatPhoneNumber } from "../../../utils/phoneNumberFormatter";
import { getSortedSceneSummeryData } from "../../../utils/visit-note/sceneSummary";
import { isOffsiteTypeOfCare } from "../../../utils/offsite/isOffsite";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "650px",
    maxWidth: "none",
  },
}));

const headerTitleFrontStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#393939",
  marginBottom: "10px",
};

interface ViewVisitNoteDialogProps {
  open: boolean;
  visitData: any;
  selectedNote?: any;
  handleClose: () => void;
}

// View-only visit-note preview. Ported from the PRP DischargeFormPreviewModel
// with all write/discharge/sign/send/attestation/print actions removed and the
// redux coupling replaced by props (visitData = the fetched `visit`).
export default function ViewVisitNoteDialog({
  open = false,
  visitData,
  selectedNote,
  handleClose,
}: ViewVisitNoteDialogProps) {
  const accordionBgColor = { backgroundColor: "#85c3f3" };
  const fullWidth = { width: "100%" };

  const dataOfDischargeForm = visitData;
  const visitNoteCareFlowData = visitData;

  const notes = dataOfDischargeForm?.visit_note || [];
  let index = notes.findIndex((obj: any) => obj?.id === selectedNote?.id);
  if (index < 0) index = notes.length ? notes.length - 1 : 0;

  const emsSceneData: any = getSortedSceneSummeryData(
    visitNoteCareFlowData?.visit_note?.[index],
    visitNoteCareFlowData,
  );

  const { demographics, initialData, iftSoapNote } = emsSceneData || {};

  const { soapAndProc } = emsSceneData || {};

  const {
    IVPlacement,
    Lead12EKG,
    MedicationAdministered,
    Other,
    Oxygen,
    airwayManagement,
    woundCare_dressing,
    Splinting,
  } = soapAndProc?.procedureDone || {};

  const { endotrachealIntubation, iCGEL } = airwayManagement || {};

  const intubationData = endotrachealIntubation;
  const iCGELData = iCGEL;

  const {
    sentToHospitalByAir,
    sentToHospitalByPOV,
    sentToHospitalByEMS,
    sentToHospitalByNSJTransportation,
  } = emsSceneData?.disposition?.dispositionData?.dispositions || {};

  // Visit note data
  const visitNote = dataOfDischargeForm?.visit_note?.[index] || {};
  // An order can bundle multiple medications/items in its item[] array, so
  // flatten to one display row per item (the columns read item[0]).
  const flattenOrderItems = (order: any) => {
    const items = order?.item?.length ? order.item : [{}];
    return items.map((it: any) => ({ ...order, item: [it] }));
  };
  const rxORowData = dataOfDischargeForm?.visit_note?.[index]?.order
    ?.filter((data: any) => data?.type === "Rx Order")
    ?.flatMap(flattenOrderItems);
  const noneRXRowData = dataOfDischargeForm?.visit_note?.[index]?.order
    ?.filter((data: any) => data?.type !== "Rx Order")
    ?.flatMap(flattenOrderItems);

  // Only the bundle VITALS are unique to the bundle (orders render in the
  // Orders tables).
  const bundleOrderData = dataOfDischargeForm?.bundleOrder?.[0];
  const bundleVitalRows = bundleOrderData?.vitals || [];
  const sections = [
    { key: "historyOfPresent", title: "History Of Present Illness" },
    { key: "objectiveNote", title: "Objective Note" },
    { key: "assessmentDescription", title: "Assessment Description" },
    { key: "plan", title: "Plan" },
    { key: "subjectiveNote", title: "Subjective Note" },
    { key: "severity", title: "Severity" },
    { key: "objectiveSymptoms", title: "Objective Symptoms" },
    { key: "description", title: "Description" },
  ];

  const getIntubationStatus = (successful?: boolean) => {
    return successful === true ? "Yes" : successful === false ? "No" : "-";
  };
  // GET TRUTH KEY
  const getTrueKey = (route: Record<string, boolean>) => {
    return (
      Object.entries(route).find((entry) => entry[1] === true)?.[0] ?? "-"
    );
  };

  const getModifiedString = (text: string) => {
    if (text) {
      return (
        text?.charAt(0)?.toUpperCase() +
        text?.substring(1)?.split("_")?.join(" ")
      );
    }
    return "-";
  };

  const patient = dataOfDischargeForm?.patient?.participant;
  const incidentDetail = dataOfDischargeForm;
  const labelFontStyle = {
    fontWeight: 500,
    color: "#727272",
    fontSize: "12px",
    marginRight: "8px",
  };
  const valueFontStyle = {
    fontWeight: 500,
    color: "#393939",
    fontSize: "12px",
    marginLeft: "8px",
  };
  const colonFontStyle = {
    fontWeight: "400",
    fontSize: "12px",
    color: "#393939",
  };

  const patientDetails = [
    {
      label: "Patient Name",
      value: `${patient?.firstName || "-"} ${patient?.lastName || ""}`,
    },
    { label: "BSA ID", value: patient?.bsaId || "-" },
    {
      label: "Date of Visit",
      value: incidentDetail?.createdAt
        ? moment(incidentDetail?.createdAt)?.format("MM/DD/YYYY")
        : "-",
    },
    {
      label: "Contact Number",
      value: patient?.contactNumber
        ? formatPhoneNumber(patient?.contactNumber)
        : "-",
    },
    { label: "Unit Number", value: patient?.baseCamp?.unitNumber || "-" },
    {
      label: "Treating Clinic",
      value:
        dataOfDischargeForm?.visit_note?.[index]?.createdProvider?.facility
          ?.name ||
        dataOfDischargeForm?.patient?.facility?.name ||
        "-",
    },
  ];

  if (visitNoteCareFlowData?.type === "Scene") {
    patientDetails.push(
      { label: "Unit Type", value: initialData?.unitType || "-" },
      {
        label: "Unit Designation",
        value: initialData?.unitDesignation || "-",
      },
      {
        label: "Date Of Birth",
        value: demographics?.dob
          ? // DOB is date-only (midnight UTC) — render in UTC so it never shifts.
            moment.utc(demographics?.dob).format("MM/DD/YYYY")
          : "",
      },
      { label: "Age", value: demographics?.age || "-" },
      { label: "Gender", value: demographics?.gender || "-" },
      { label: "Registration Id", value: demographics?.registrationId || "-" },
      {
        label: "Participant Type",
        value: demographics?.participantType || "-",
      },
    );
  }

  const incidentDetails = [
    {
      label: "Incident Date",
      value: incidentDetail?.incidentDate
        ? // Date-only value (midnight UTC) — render in UTC so it never shifts.
          moment.utc(incidentDetail?.incidentDate)?.format("MM/DD/YYYY")
        : "-",
    },
    { label: "Incident Time", value: incidentDetail?.incidentTime || "-" },
    {
      label: "Incident Location",
      value: incidentDetail?.incidentlocation || "-",
    },
    { label: "Witness Name", value: incidentDetail?.witnessName || "-" },
  ];

  if (visitNoteCareFlowData?.type === "Scene") {
    incidentDetails.push({
      label: "Witness Contact Number",
      value: incidentDetail?.witnessContact
        ? formatPhoneNumber(incidentDetail?.witnessContact)
        : "-",
    });
  }

  const ICDCodeColumns = [
    {
      field: "srNo",
      label: <div>ICD 10 Code</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.label}</p>
      ),
    },
    {
      field: "details",
      label: <div className="">Details</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.description}</p>
      ),
    },
  ];

  const bundleVitalColumns = [
    {
      field: "vital",
      label: <div>Vital</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.vitalName}</p>
      ),
    },
    {
      field: "frequency",
      label: <div>Frequency</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.frequency || "-"}</p>
      ),
    },
  ];

  const RXOrderColumns = [
    {
      field: "srNo",
      label: <div></div>,
      renderLogic: (row: any, i: any) => (
        <p className="text-neutral-90 font-normal">{i + 1}</p>
      ),
    },
    {
      field: "condition",
      label: <div className="w-[10vw]">Condition</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.item?.[0]?.conditionName ||
            dataOfDischargeForm?.chiefComplaints ||
            "-"}
        </p>
      ),
    },
    {
      field: "medication",
      label: <div className="w-[10vw]">Medication</div>,
      renderLogic: (row: any) => (
        <p className=" font-normal">{row?.item?.[0]?.medicationName || "-"}</p>
      ),
    },
    {
      field: "qty",
      label: <div className="w-[5vw]">Qty</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.item?.[0]?.quantity || "-"}
        </p>
      ),
    },
    {
      field: "unit",
      label: <div className="w-[5vw]">Unit</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.item?.[0]?.unit || "-"}
        </p>
      ),
    },
    {
      field: "frequency",
      label: <div className="w-[5vw]">Frequency</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.item?.[0]?.frequency || row?.frequency || "-"}
        </p>
      ),
    },
    {
      field: "details",
      label: <div className="w-[20vw]">Details</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.note || "-"}</p>
      ),
    },
    {
      field: "status",
      label: <div className="w-[10vw]">Status</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.status ? row?.status?.split("_")?.join(" ") : "-"}
        </p>
      ),
    },
  ];

  const nonRXColumns = [
    {
      field: "srNo",
      label: <div></div>,
      renderLogic: (row: any, i: any) => (
        <p className="text-neutral-90 font-normal">{i + 1}</p>
      ),
    },
    {
      field: "orderType",
      label: <div className="w-[10vw]">Order Type</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.type?.split("_")?.join(" ") || "-"}</p>
      ),
    },
    {
      field: "Items",
      label: <div className="w-[10vw]">Items</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.item?.[0]?.name || "-"}</p>
      ),
    },
    {
      field: "details",
      label: <div className="w-[50vw]">Details</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.note}</p>
      ),
    },
    {
      field: "status",
      label: <div className="w-[10vw]">Status</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.status ? row?.status?.split("_")?.join(" ") : "-"}
        </p>
      ),
    },
  ];

  const vitalsColumns = [
    {
      field: "Pulse",
      label: <div>Pulse</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.pulse}</p>
      ),
    },
    {
      field: "SBP",
      label: <div className="">SBP</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.SBP}</p>
      ),
    },
    {
      field: "DBP",
      label: <div className="">DBP</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.DBP}</p>
      ),
    },
    {
      field: "Resp Rate",
      label: <div>Resp Rate</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.respirationRate}</p>
      ),
    },
    {
      field: "SpO2",
      label: <div>SpO2</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.SPO2}</p>
      ),
    },
    {
      field: "GCS",
      label: <div className="">GCS</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.GCS}</p>
      ),
    },
    {
      field: "Time",
      label: <div className="">Time</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.time}</p>
      ),
    },
  ];

  const iVInsertionColumns = [
    {
      field: "Size",
      label: <div>Size</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.size}</p>
      ),
    },
    {
      field: "Attempts",
      label: <div className="">Attempts</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.attempts}</p>
      ),
    },
    {
      field: "Successful",
      label: <div className="">Successful</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.successful}</p>
      ),
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.time}</p>
      ),
    },
  ];

  const medicationAdministeredColumns = [
    {
      field: "Medication",
      label: <div>Medication</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.medication}</p>
      ),
    },
    {
      field: "Dose (mg)",
      label: <div className="">Dose (mg)</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.dose}</p>
      ),
    },
    {
      field: "Route",
      label: <div className="">Route</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.route}</p>
      ),
    },
    {
      field: "Response",
      label: <div>Response</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.response}</p>
      ),
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.time}</p>
      ),
    },
  ];

  const oxygenColumns = [
    {
      field: "Device",
      label: <div>Device</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.device}</p>
      ),
    },
    {
      field: "LPM",
      label: <div className="">LPM</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.LPM}</p>
      ),
    },
    {
      field: "Improvement",
      label: <div>Improvement</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.improvement}</p>
      ),
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.time}</p>
      ),
    },
  ];

  const iGelInsertionColumn = [
    {
      field: "Size",
      label: <div>Size</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.size}</p>
      ),
    },
    {
      field: "Attempts",
      label: <div className="">Attempts</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.attempts}</p>
      ),
    },
    {
      field: "Successful",
      label: <div className="">successful</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.successful}</p>
      ),
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.time}</p>
      ),
    },
  ];

  const endIntColumn = [
    {
      field: "Attempts",
      label: <div>Attempts</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.attempts}</p>
      ),
    },
    {
      field: "Blade Size",
      label: <div className="">Blade Size</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.bladeSize}</p>
      ),
    },
    {
      field: "Device",
      label: <div className="">Device</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.device}</p>
      ),
    },
    {
      field: "Tube Size",
      label: <div className="">Tube Size</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.tubeSize}</p>
      ),
    },
    {
      field: "Successful",
      label: <div className="">successful</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.successful}</p>
      ),
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.time}</p>
      ),
    },
  ];

  const endIntRowData = [
    {
      attempts: intubationData?.attempts || "-",
      bladeSize: intubationData?.bladeSize || "-",
      device: intubationData?.device || "-",
      tubeSize: intubationData?.tubeSize || "-",
      successful: getIntubationStatus(intubationData?.successful),
      time: intubationData?.time || "-",
    },
  ];

  const iGelInsertionRowData = [
    {
      size: iCGELData?.size || "-",
      attempts: iCGELData?.attempts || "-",
      successful: getIntubationStatus(iCGELData?.successful),
      time: iCGELData?.time || "-",
    },
  ];

  const vitalsRowData = emsSceneData?.soapAndProc?.vitals?.map((data: any) => ({
    pulse: data?.heartRate || "-",
    SBP: data?.systolic || "-",
    DBP: data?.diastolic || "-",
    respirationRate: data?.respirationRate || "-",
    SPO2: data?.oxygenSaturation || "-",
    GCS: data?.GCS || "-",
    time: data?.time || "-",
  }));

  const oxygenRowData = Oxygen?.[0]?.IVs?.map((data: any) => ({
    device: data?.device || "-",
    LPM: data?.LPM || "-",
    DBP: data?.DBP || "-",
    improvement: data?.improvement || "-",
    time: data?.time || "-",
  }));

  const medicationAdministeredColumnsRowData =
    MedicationAdministered?.[0]?.meds?.map((data: any) => ({
      medication: data?.medication || "-",
      dose: data?.dose || "-",
      route: getTrueKey(data?.route),
      response: data?.response?.Improve
        ? "Improve"
        : data?.response?.nochange
          ? "No Change"
          : data?.response?.worsen
            ? "Worsen"
            : "-",
      time: data?.response?.time || "-",
    }));

  const iVInsertionRowData = IVPlacement?.[0]?.data?.map((data: any) => ({
    size: data?.size || "-",
    attempts: data?.attempts || "-",
    successful: data?.successful || "-",
    time: data?.time || "-",
  }));

  // View-only: diagnosis comes from the saved note's assessment.
  const selectedDiagnosis =
    visitNoteCareFlowData?.visit_note?.[index]?.assessment?.map(
      (data: any, i: number) => ({
        id: data?.code,
        index: i,
        label: data?.code,
        value: data?.code,
        description: data?.desc,
      }),
    ) || [];
  const rowData = selectedDiagnosis;

  return (
    <React.Fragment>
      <BootstrapDialog
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <div
          className="flex justify-between items-center"
          style={{ padding: "12px 16px" }}
        >
          <div
            className="font-weight-500 font-size-18px"
            style={{ visibility: "hidden" }}
          >
            Modal title
          </div>
          <div>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={(theme) => ({
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <DialogContent dividers>
          <div
            style={{
              height: "-webkit-fill-available",
              overflow: "auto",
            }}
          >
            {/* Heading */}
            <header className="flex items-center justify-between p-3">
              <div className="flex items-center gap-2">
                <img src={jamboree2027PreviewLogo} alt="jamboree logo" />
                <div className="font-weight-900 font-size-20px color-393939">
                  JAMBOREE 2026
                </div>
              </div>
              <div className="font-weight-650 font-size-18px color-005596">
                JamboCare
              </div>
            </header>
            {/* Type */}
            <div className="background-005596 color-FFFFFF font-weight-500 font-size-14px text-align-center p-1">
              {dataOfDischargeForm?.typeOfCare === "Self_Care"
                ? "Self Care"
                : dataOfDischargeForm?.typeOfCare
                      ?.toLowerCase()
                      ?.includes("nurse")
                  ? "Nurse Care"
                  : dataOfDischargeForm?.typeOfCare
                        ?.toLowerCase()
                        ?.includes("mental")
                    ? "Mental Health Care"
                    : dataOfDischargeForm?.typeOfCare
                          ?.toLowerCase()
                          ?.includes("ear")
                      ? "Listening Ear Care"
                      : dataOfDischargeForm?.typeOfCare === "Physician"
                        ? "Physician Care"
                        : dataOfDischargeForm?.typeOfCare === null
                          ? "EMS Scene "
                          : dataOfDischargeForm?.typeOfCare}{" "}
              Visit
            </div>
            {/* Patient Details */}
            <div className="pt-3 pl-4 pb-4 pr-4">
              <Heading
                title="Patient Details"
                customStyle={headerTitleFrontStyle}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  rowGap: "10px",
                }}
              >
                {patientDetails?.map((data, i) => (
                  <div key={i}>
                    <span style={labelFontStyle}>{data?.label}</span>
                    <span style={colonFontStyle}>:</span>
                    <span style={valueFontStyle}>{data?.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <hr />
            {/* Incident Details */}
            <div className="pt-3 pl-4 pb-4 pr-4">
              <Heading
                title="Incident Details"
                customStyle={headerTitleFrontStyle}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  rowGap: "10px",
                }}
              >
                {incidentDetails?.map((data, i) => (
                  <div key={i}>
                    <span style={labelFontStyle}>{data?.label}</span>
                    <span style={colonFontStyle}>:</span>
                    <span style={valueFontStyle}>{data?.value}</span>
                  </div>
                ))}
              </div>
              {visitNoteCareFlowData?.type === "Scene" && (
                <div className="pt-5 pb-4 pr-4">
                  <Heading
                    title="BRIEF factual description of incident:"
                    customStyle={labelFontStyle}
                  />
                  <div
                    style={{ ...valueFontStyle, marginLeft: 0 }}
                    className="font-weight-500 color-979797 font-size-12px mt-2"
                  >
                    {incidentDetail?.chiefComplaints || "-"}
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Chief Complaint */}
            {visitNoteCareFlowData?.type !== "Scene" && (
              <div className="pt-3 pl-4 pb-4 pr-4">
                <Heading
                  title="Chief Complaint"
                  customStyle={headerTitleFrontStyle}
                />
                <div className="font-weight-500 color-979797 font-size-12px">
                  {dataOfDischargeForm?.chiefComplaints}
                </div>
              </div>
            )}
            <hr />
            {/* dynamic note sections */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
              {sections?.map(
                ({ key, title }) =>
                  visitNote[key] && (
                    <VisitNoteSection
                      key={key}
                      title={title}
                      content={visitNote[key]}
                    />
                  ),
              )}
            </div>

            {/* Diagnosis — hidden for Listening Ear and Offsite Transfers */}
            {!dataOfDischargeForm?.typeOfCare?.toLowerCase()?.includes("ear") &&
              !isOffsiteTypeOfCare(dataOfDischargeForm?.typeOfCare) && (
                <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                  <Heading
                    title="Diagnosis"
                    customStyle={headerTitleFrontStyle}
                  />
                  <div>
                    <Table columns={ICDCodeColumns} rows={rowData} />
                  </div>
                </div>
              )}

            {/* Bundle Order Vitals */}
            {bundleVitalRows.length > 0 && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Bundle Order Vitals"
                  customStyle={headerTitleFrontStyle}
                />
                <div className="mt-2">
                  <Table columns={bundleVitalColumns} rows={bundleVitalRows} />
                </div>
              </div>
            )}

            {visitNoteCareFlowData?.visit_note?.[index]?.dischargeNote && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Discharge Instructions"
                  customStyle={headerTitleFrontStyle}
                />
                <div
                  className="font-weight-500 color-979797 font-size-12px"
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "12px 16px",
                    borderRadius: "6px",
                    border: "1px solid #e9ecef",
                    lineHeight: "1.5",
                    whiteSpace: "pre-wrap",
                    color: "rgb(57, 57, 57)",
                  }}
                >
                  {visitNoteCareFlowData?.visit_note?.[index]?.dischargeNote}
                </div>
              </div>
            )}

            {rxORowData?.length > 0 && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Rx Order"
                  customStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#-",
                    marginBottom: "8px",
                  }}
                />
                <Table
                  columns={RXOrderColumns}
                  rows={rxORowData}
                  noDataFoundMessage={"No ICD 10 or DSM Code entered"}
                />
              </div>
            )}
            {noneRXRowData?.length > 0 && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Non Rx Order"
                  customStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#-",
                    marginBottom: "8px",
                  }}
                />
                <Table
                  columns={nonRXColumns}
                  rows={noneRXRowData}
                  noDataFoundMessage={"No Data"}
                />
              </div>
            )}

            {/* EMS Scene ACCORDIONS */}
            {visitNoteCareFlowData?.type === "Scene" &&
              !visitNoteCareFlowData?.visit_note?.[index]?.assessment && (
                <div className="mt-8 flex flex-col gap-3 ">
                  {visitNoteCareFlowData?.unitType !==
                    "Military FLA-Interfacility Transport_ONLY" && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        SOAP and Procedures
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div className="p-3">
                          <div className="w-[60%] pb-3">
                            <Heading
                              title="Chief Complaint"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="font-weight-500 color-979797 font-size-12px">
                              {emsSceneData?.soapAndProc?.chiefComplaints}
                            </div>
                          </div>
                          <hr />
                          <div className="pt-5 pb-3">
                            <Heading
                              title="Subjective"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="font-weight-500 color-979797 font-size-12px">
                              {emsSceneData?.soapAndProc?.subjectiveNote}
                            </div>
                          </div>
                          <hr />
                          <div className="mt-5">
                            <Heading
                              title="Vital Signs - Please Scroll To see all fields"
                              customStyle={headerTitleFrontStyle}
                            />
                            <Table
                              columns={vitalsColumns}
                              rows={vitalsRowData}
                              noDataFoundMessage={"No Data"}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className="pt-5 pb-3">
                          <Heading
                            title="Objective/Exam Findings"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {emsSceneData?.soapAndProc?.objectiveNote}
                          </div>
                        </div>
                        <hr />
                        <div className="pt-5  pb-3">
                          <Heading
                            title="Assessment and Plan & Treatment"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {emsSceneData?.soapAndProc?.assessment}
                          </div>
                        </div>
                        <hr />
                        <div className="pt-5">
                          <Heading
                            title="Procedures Done"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="flex flex-col gap-2">
                            {airwayManagement?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Airway Management
                              </div>
                            )}
                            {woundCare_dressing?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Wound Care/Dressing
                              </div>
                            )}
                            {IVPlacement?.[0]?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                IV Placement
                              </div>
                            )}
                            {MedicationAdministered?.[0]?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Medication Administered
                              </div>
                            )}
                            {Lead12EKG?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                12 Lead EKG
                              </div>
                            )}
                            {Splinting?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Splinting
                              </div>
                            )}
                            {Oxygen?.[0]?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Oxygen
                              </div>
                            )}
                            {Other?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Other
                              </div>
                            )}
                            {Other?.select && (
                              <div className="mt-5 pb-3 ">
                                <Heading
                                  title="Other Procedure Done"
                                  customStyle={headerTitleFrontStyle}
                                />
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  {Other?.description || "-"}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <hr />
                        {airwayManagement?.select && (
                          <div className="pt-5 pb-3">
                            <Heading
                              title="Airway Management"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="flex flex-col gap-2">
                              {airwayManagement?.bvm && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  BVM
                                </div>
                              )}
                              {airwayManagement?.suctioning && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  Suctioning
                                </div>
                              )}
                              {airwayManagement?.OPA && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  OPA
                                </div>
                              )}
                              {airwayManagement?.NPA && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  NPA
                                </div>
                              )}
                              {iCGEL?.select && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  iGel
                                </div>
                              )}
                              {endotrachealIntubation?.select && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  Endotracheal Intubation
                                </div>
                              )}
                              {airwayManagement?.other?.select && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  Other
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <hr />
                        {airwayManagement?.select &&
                          airwayManagement?.other?.select && (
                            <>
                              <div className="mt-5 pb-3">
                                <Heading
                                  title="Other Airway Management"
                                  customStyle={headerTitleFrontStyle}
                                />
                                <div className="flex gap-8 mb-2">
                                  <div className="font-weight-500 color-979797 font-size-12px">
                                    {airwayManagement?.other?.desription || "-"}
                                  </div>
                                </div>
                              </div>
                              <hr />
                            </>
                          )}
                        {airwayManagement?.select &&
                          endotrachealIntubation?.select && (
                            <>
                              <div className="mt-5">
                                <Heading
                                  title="Endotracheal Intubation"
                                  customStyle={headerTitleFrontStyle}
                                />
                                <Table
                                  columns={endIntColumn}
                                  rows={endIntRowData}
                                  noDataFoundMessage={"No Data"}
                                />
                              </div>
                              <hr />
                            </>
                          )}
                        {airwayManagement?.select && iCGEL?.select && (
                          <>
                            <div className="mt-5 pb-3">
                              <Heading
                                title="iGel Insertion"
                                customStyle={headerTitleFrontStyle}
                              />
                              <Table
                                columns={iGelInsertionColumn}
                                rows={iGelInsertionRowData}
                                noDataFoundMessage={"No Data"}
                              />
                            </div>
                            <hr />
                          </>
                        )}
                        {IVPlacement?.[0]?.select && (
                          <>
                            <div className="mt-5">
                              <Heading
                                title="IV Insertion"
                                customStyle={headerTitleFrontStyle}
                              />
                              <Table
                                columns={iVInsertionColumns}
                                rows={iVInsertionRowData}
                                noDataFoundMessage={"No Data"}
                              />
                            </div>
                            <hr />
                          </>
                        )}
                        {MedicationAdministered?.[0]?.select && (
                          <>
                            <div className="mt-5">
                              <Heading
                                title="Medication Administered"
                                customStyle={headerTitleFrontStyle}
                              />
                              <Table
                                columns={medicationAdministeredColumns}
                                rows={medicationAdministeredColumnsRowData}
                                noDataFoundMessage={"No Data"}
                              />
                            </div>
                            <hr />
                          </>
                        )}
                        {Oxygen?.[0]?.select && (
                          <>
                            <div className="mt-5 pb-5">
                              <Heading
                                title="Oxygen Administration"
                                customStyle={headerTitleFrontStyle}
                              />
                              <Table
                                columns={oxygenColumns}
                                rows={oxygenRowData}
                                noDataFoundMessage={"No Data"}
                              />
                            </div>
                            <hr />
                          </>
                        )}
                        {Lead12EKG?.select && (
                          <div className="pt-5">
                            <Heading
                              title="12 Lead Interpretation"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="font-weight-500 color-979797 font-size-12px">
                              {emsSceneData?.soapAndProc?.procedureDone
                                ?.Lead12EKG?.lead12Interpration || "-"}
                            </div>
                          </div>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {visitNoteCareFlowData?.unitType ===
                    "Military FLA-Interfacility Transport_ONLY" && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        IFT SOAP Note
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div>
                          <div className="pt-5">
                            <Heading
                              title="Please include a brief SOAP note of your transport, including vital signs, if necessary."
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="font-weight-500 color-979797 font-size-12px">
                              {iftSoapNote}
                            </div>
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Diagnosis and Disposition */}
                  <Accordion
                    className="important-content"
                    expanded
                    sx={accordionBgColor}
                    style={fullWidth}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      Diagnosis and Disposition{" "}
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: "white" }}>
                      <div className="p-3">
                        <div className="pt-5 pb-3">
                          <Heading
                            title="Impression/Diagnosis"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.disposition?.dispositionData
                                ?.impressionDiagnosis,
                            )}
                          </div>
                        </div>
                        <hr />
                        <div className="pt-5">
                          <Heading
                            title="Transferring Unit"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.disposition?.dispositionData
                                ?.transferringUnit,
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionDetails>
                  </Accordion>

                  {/* Treat and Release */}
                  {emsSceneData?.disposition?.dispositionData?.dispositions
                    ?.treatedAndReleased?.select && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Treat and Release
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div className="p-3 text-[#004981]">
                          <h3 style={headerTitleFrontStyle}>
                            I, the treating EMS Clinician, certify that this
                            individual is greater that 18 years of age, or in the
                            care of someone 18 years of age or older.
                          </h3>
                          <div className="pt-3"></div>
                        </div>
                        <div className="pt-5 pb-5">
                          <hr />
                        </div>
                        <div className="p-3 text-[#004981]">
                          <h3 style={headerTitleFrontStyle}>
                            Neither I nor the attending EMT(s) and/or paramedic(s)
                            believe that I, my child, or the scout I am supervising
                            have an illness or injury requiring EMS transport to a
                            Jamboree Medical Facility.
                          </h3>
                          <div className="w-[30%] pt-7">
                            <Heading
                              title="Treat and Release Signature"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="pt-3 pb-4 pr-4 w-max">
                              <div
                                style={{
                                  background: "#F4F4F4",
                                  padding: "10px 20px",
                                }}
                                className="text-center border-radius-8px"
                              >
                                <div
                                  style={{
                                    fontSize: "32px",
                                    fontFamily: "Sacramento",
                                  }}
                                >
                                  {
                                    emsSceneData?.disposition?.dispositionData
                                      ?.dispositions?.treatedAndReleased?.name
                                  }
                                </div>
                                <div className="font-weight-500 font-size-12px color-565656 ">
                                  Signature
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Refusal of Medical Aid/Transport */}
                  {emsSceneData?.disposition?.dispositionData?.dispositions
                    ?.refusalOfMedicalAid && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Refusal of Medical Aid/Transport
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div className="p-3 text-[#004981]">
                          <h3 style={headerTitleFrontStyle}>
                            I hereby acknowledge and release the above-mentioned
                            emergency service provider and its personnel of any
                            liability for my refusal to accept further treatment.
                          </h3>
                          <div className="w-[30%] pt-7">
                            <Heading
                              title="RMA Release:"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="pt-3 pb-4 pr-4">
                              <div
                                style={{
                                  background: "#F4F4F4",
                                  padding: "10px 20px",
                                }}
                                className="text-center border-radius-8px"
                              >
                                <div
                                  style={{
                                    fontSize: "32px",
                                    fontFamily: "Sacramento",
                                  }}
                                >
                                  {
                                    emsSceneData?.disposition?.refusalOfMedicalAid
                                      ?.rmaRelease
                                  }
                                </div>
                                <div className="font-weight-500 font-size-12px color-565656 ">
                                  Signature
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <hr />
                        <div>
                          <div className="w-[30%] pt-7">
                            <Heading
                              title="Witness Signature"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="pt-3 pb-4 pr-4">
                              <div
                                style={{
                                  background: "#F4F4F4",
                                  padding: "10px 20px",
                                }}
                                className="text-center border-radius-8px"
                              >
                                <div
                                  style={{
                                    fontSize: "32px",
                                    fontFamily: "Sacramento",
                                  }}
                                >
                                  {
                                    emsSceneData?.disposition?.refusalOfMedicalAid
                                      ?.witnessName
                                  }
                                </div>
                                <div className="font-weight-500 font-size-12px color-565656 ">
                                  Signature
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Off Site Transfer */}
                  {(sentToHospitalByAir ||
                    sentToHospitalByPOV ||
                    sentToHospitalByEMS ||
                    sentToHospitalByNSJTransportation) && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Off Site Transfer{" "}
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div className="w-[30%] pt-3 pb-3">
                          <Heading
                            title="Hospital/Off Site Facility"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.disposition?.hospitalName,
                            )}
                          </div>
                        </div>
                        <hr />
                        <div className="w-[30%] pt-5">
                          <Heading
                            title="Transferring Unit"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.disposition?.transferringUnit,
                            )}
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Signature */}
                  <Accordion
                    className="important-content"
                    expanded
                    sx={accordionBgColor}
                    style={fullWidth}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      Signature
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: "white" }}>
                      <div>
                        <div className="pt-5">
                          <Heading
                            title="Time Call End"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {emsSceneData?.signature?.timeCallEnd || "-"}
                          </div>
                        </div>
                        <div className="pt-5">
                          <Heading
                            title="Primary Treating Clinician"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.signature?.primaryClinic,
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-[30%] pt-7">
                        <Heading
                          title="Signature"
                          customStyle={headerTitleFrontStyle}
                        />
                        <div className="pt-3 pb-4 pr-4">
                          <div
                            style={{
                              background: "#F4F4F4",
                              padding: "10px 20px",
                            }}
                            className="text-center border-radius-8px"
                          >
                            <div
                              style={{
                                fontSize: "32px",
                                fontFamily: "Sacramento",
                              }}
                            >
                              {emsSceneData?.signature?.signature || "-"}{" "}
                            </div>
                            <div className="font-weight-500 font-size-12px color-565656 ">
                              Signature
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="pt-3 pb-5">
                        <hr />
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
              )}

            {/* Read-only signed-by signature */}
            {visitNoteCareFlowData?.visit_note?.[index]?.isVisitSign &&
              visitNoteCareFlowData?.visit_note?.[index]?.signedBy?.id && (
                <div className="pt-3 pl-4 pb-4 pr-4 text-end flex justify-end important-content">
                  <div
                    style={{
                      background: "#F4F4F4",
                      padding: "10px 20px",
                    }}
                    className="text-center border-radius-8px"
                  >
                    <div style={{ fontSize: "32px", fontFamily: "Sacramento" }}>
                      {visitNoteCareFlowData?.visit_note?.[index]?.signedBy
                        ?.firstName +
                        " " +
                        visitNoteCareFlowData?.visit_note?.[index]?.signedBy
                          ?.lastName}
                    </div>
                    <div className="font-weight-500 font-size-12px color-565656 ">
                      Signature
                    </div>
                  </div>
                </div>
              )}
          </div>
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
}

const VisitNoteSection = ({
  title,
  content,
}: {
  title: string;
  content: string;
}) => (
  <div className="pt-3 pl-4 pb-4 pr-4">
    <Heading title={title} customStyle={headerTitleFrontStyle} />
    <div className="font-weight-500 color-979797 font-size-12px">
      {content?.charAt(0)?.toUpperCase() + content?.substring(1)}
    </div>
  </div>
);
